import type { KeyboardEvent, Ref } from 'react'
import {
  GO_TO_MESSAGE,
  GoToStatus,
  type GoToState,
  type RequisitionMatch,
} from '../lib/goToRequisition.ts'
import { GO_TO_REQUISITION_KEY } from '../lib/shortcuts.ts'
import { titleCaseName } from '../lib/titleCase.ts'
import { icons } from './icons.ts'
import { Input } from './Input.tsx'

const KBD =
  'border-stroke-weak bg-fill-weak text-text-strong inline-block rounded-[4px] border px-1 text-[11px] leading-4 font-medium'

/**
 * The "Ir para conferência" field as it is drawn, for whatever state it is in.
 *
 * What hangs beneath it is laid over the page rather than in the header's
 * flow: a message or a list that pushed the conferência down would move the
 * rows under somebody's hands.
 */
export function GoToRequisitionField({
  value,
  state,
  inputRef,
  onChange,
  onSubmit,
  onKeyDown,
  onPick,
  onDismiss,
}: {
  value: string
  state: GoToState
  inputRef?: Ref<HTMLInputElement> | undefined
  onChange: (value: string) => void
  onSubmit: () => void
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  onPick: (match: RequisitionMatch) => void
  onDismiss: () => void
}) {
  // A refusal is in the server's own words; the rest are ours.
  const message = state.status === GoToStatus.Refused ? state.message : GO_TO_MESSAGE[state.status]
  const choosing = state.status === GoToStatus.Choosing
  const fetching = state.status === GoToStatus.Fetching

  return (
    <form
      role="search"
      className="relative ml-auto w-64 shrink-0"
      onSubmit={(event) => {
        event.preventDefault()
        // Selected, so the next scan replaces this one instead of joining it.
        event.currentTarget.querySelector('input')?.select()
        onSubmit()
      }}
    >
      <Input
        ref={inputRef}
        size="small"
        // Digits only, and a reader types them: no suggestions over the list.
        inputMode="numeric"
        autoComplete="off"
        aria-label="Ir para conferência"
        placeholder="Ir para conferência"
        leadingIcon={state.status === GoToStatus.Searching || fetching ? icons.busy : icons.search}
        suffixAdornment={<kbd className={KBD}>{GO_TO_REQUISITION_KEY}</kbd>}
        aria-invalid={message !== undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={(event) => event.currentTarget.select()}
        onBlur={onDismiss}
      />
      {fetching && (
        // Said, because the ERP takes seconds where the queue takes none.
        <p
          role="status"
          className="rounded-base border-stroke-weak bg-background-raised shadow-raised text-tSmall text-text-weak absolute top-full right-0 left-0 z-10 mt-1 border px-3 py-2"
        >
          Buscando no ERP…
        </p>
      )}
      {message !== undefined && (
        <p
          role="status"
          className="rounded-base border-stroke-weak bg-background-raised shadow-raised text-tSmall text-text-error absolute top-full right-0 left-0 z-10 mt-1 border px-3 py-2"
        >
          {message}
        </p>
      )}
      {choosing && (
        <ul
          role="listbox"
          aria-label="Em qual filial"
          className="rounded-base border-stroke-weak bg-background-raised shadow-raised absolute top-full right-0 left-0 z-10 mt-1 flex flex-col border py-1"
        >
          {state.matches.map((match, at) => (
            <li
              key={match.branchCode}
              role="option"
              aria-selected={at === state.active}
              className={`text-tP flex cursor-pointer flex-col px-3 py-1 ${
                at === state.active ? 'bg-fill-hover' : 'hover:bg-fill-hover'
              }`}
              // The field's blur puts the list away; a press on an option must
              // not blur it first, or the click lands on nothing.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(match)}
            >
              <span className="text-text-strong">
                Filial {match.branchName ?? match.branchCode} · Orçamento {match.quotationNumber}
              </span>
              <span className="text-tSmall text-text-weak truncate">
                {match.inQueue
                  ? titleCaseName(match.patientName) || 'Paciente não identificado'
                  : 'Buscar no ERP'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </form>
  )
}
